//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import {
  IMultiHandleRightsGraphApiRequest,
  IMultiHandleRightsRequestApiResponse,
} from '@apis/rights-management/rightsManagementApiModels';
import Cuid from '@models/Cuid';
import { IRightsRequest } from '@uia/rights-request/rights-request-model';
import { serializedGatewayFetch, sessionFetch } from '@utils/sessionFetch';
import { ISerializedResponse } from '@utils/types/SerializedResponse';

const UIA_BASE_URL = '/api/universal-image-actions';
const UIA_MULTI_HANDLE_RIGHTS_V2_URL = '/api/v2/universal-image-actions/items';

const webGatewayFetch = (
  endpoint: string,
  init?: RequestInit,
): Promise<ISerializedResponse> => {
  return serializedGatewayFetch('web_gateway', endpoint, init);
};

/**
 * Request rights on a cuid not using the graph api
 * POST /api/universal-image-actions/:cuid/request-rights
 *
 * @param cuid: Cuid
 * @param params: <IRightsRequest> The rights request message to send
 *
 * @return Promise<string>: the cuid's current rights request status
 */
export const requestRights = async (
  cuid: Cuid,
  params: IRightsRequest,
): Promise<string> => {
  const url = `${UIA_BASE_URL}/${cuid}/request-rights`;
  const res = await sessionFetch(url, {
    method: 'POST',
    body: JSON.stringify(params),
  });

  return await res.json();
};

/**
 * Requests rights on a cuid using the graph api for Multi handle
 * api/v2/universal-image-actions/items/:cuid/graph-rights-request
 *
 * @param partialCuid: ItemType + ItemId of the item to request rights for
 * @param params: <IRightRequest> The rights request message to send
 */
export const requestMultiHandleRightsGraphApi = async (
  req: IMultiHandleRightsGraphApiRequest,
  init?: RequestInit,
): Promise<IMultiHandleRightsRequestApiResponse> => {
  const { params, partialCuid } = req;
  const url = `${UIA_MULTI_HANDLE_RIGHTS_V2_URL}/${partialCuid}/graph-rights-request`;
  const res = await webGatewayFetch(url, {
    method: 'POST',
    body: JSON.stringify(params),
    ...init,
  });
  const rightsRequestState =
    await res.json<IMultiHandleRightsRequestApiResponse>();
  return rightsRequestState;
};
